import { useFoodAvoids } from "@/hooks/features/use-food-avoids";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { IonContent, IonPage } from "@ionic/react";
import { useRef, useState } from "react";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";
import { SearchDislikeFoodModal } from "../../components/search-dislike-food-modal";
import { SelectableChip } from "../../components/selectable-chip";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const NONE_OPTION = "None";
const OTHER_OPTION = "Other";

const OnboardingFoodAvoidsV2Page = () => {
  const pageRef = useRef<HTMLElement>(null);
  const { info, navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const {
    sortedFoodList,
    selectedFoods,
    setSelectedFoods,
    customFoods,
    addCustomFood,
    removeCustomFood,
    handleSubmit,
    isUpdating,
  } = useFoodAvoids(() => {
    setSmartlookOnboardingEvent({ type: "submit", stepPath: info?.path });
    setMixpanelOnboardingEvent({ type: "submit", stepPath: info?.path });
    navigateToNextStep();
  });

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const toggle = (food: string) => {
    if (customFoods.includes(food)) {
      removeCustomFood(food);
      return;
    }
    setSelectedFoods((prev) => {
      const next = new Set(prev);
      if (food === NONE_OPTION) {
        if (next.has(NONE_OPTION)) {
          next.delete(NONE_OPTION);
        } else {
          next.clear();
          next.add(NONE_OPTION);
        }
        return next;
      }
      if (next.has(food)) {
        next.delete(food);
      } else {
        next.delete(NONE_OPTION);
        next.add(food);
      }
      return next;
    });
  };

  const onAdvance = async () => {
    if (isUpdating) return;
    await preloadNextRoute();
    handleSubmit();
  };

  const orderedList = sortedFoodList.includes(NONE_OPTION)
    ? [NONE_OPTION, ...sortedFoodList.filter((f) => f !== NONE_OPTION)]
    : sortedFoodList;

  return (
    <IonPage ref={pageRef}>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/food-avoids.svg"
          title="Any foods you'd rather avoid?"
          subtitle="Select as many as you'd like"
        >
          <div className="w-full max-w-[321px] grid grid-cols-2 gap-4">
            {orderedList.map((food) => (
              <SelectableChip
                key={food}
                label={food}
                selected={selectedFoods.has(food)}
                onClick={() => toggle(food)}
                disabled={isUpdating}
              />
            ))}
            <SelectableChip
              label={OTHER_OPTION}
              selected={false}
              onClick={() => setIsSearchOpen(true)}
              disabled={isUpdating}
            />
          </div>
          <OnboardingNextFab onClick={onAdvance} disabled={isUpdating} />
        </OnboardingStepShell>

        <SearchDislikeFoodModal
          pageRef={pageRef}
          isOpen={isSearchOpen}
          onDismiss={() => setIsSearchOpen(false)}
          onSave={(foods) => foods.forEach((food) => addCustomFood(food))}
        />
      </IonContent>
    </IonPage>
  );
};

export default OnboardingFoodAvoidsV2Page;
